<template>
  <ReportWrapper
    :loading="loading"
    :on-load="loadReport"
    :tab-lv1-index="tabLv1Index"
    :tab-lv2-index="tabLv2Index"
    :showProviderFilter="isAdmin"
    :showChairAmountForm="isAdmin"
  >
    <ReportTable minWidth="760px">
      <thead>
        <tr>
          <th><span class="white--text">Name</span></th>
          <th>
            <TableHeaderWithTooltip
              title="Productive hours"
              :tooltip="tooltips['Productive hours']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Chair utilization hours"
              :tooltip="tooltips['Chair utilization hours']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Chair utilization %"
              :tooltip="tooltips['Chair utilization %']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Untapped potential $"
              :tooltip="tooltips['Untapped potential $']"
            />
          </th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <!-- clientAdmin view -->
        <template v-if="isAdmin && dataPerProvider.length">
          <tr
            v-for="(item, index) in dataPerProvider"
            :key="index"
            :class="{
              active: item.isCurrentUserRecord,
              median: item.isMedian,
              hidden: !item.isActive || item.isHidden || item.isHiddenReport,
            }"
          >
            <td>{{ item.fullName }}</td>
            <td></td>
            <td></td>
            <td>{{ formatPercentage(item.chairUtilizationRate) }}</td>
            <td></td>
          </tr>

          <tr
            v-if="!userHasRecordInReport"
            class="median"
          >
            <td>Median</td>
            <td></td>
            <td></td>
            <td>{{ formatPercentage(median.chairUtilizationRate) }}</td>
            <td></td>
          </tr>
        </template>

        <!-- clientUser view -->
        <template v-else-if="!isAdmin && userRecord">
          <tr class="active">
            <td>{{ userRecord.fullName }}</td>
            <td></td>
            <td></td>
            <td>{{ formatPercentage(userRecord.chairUtilizationRate) }}</td>
            <td></td>
          </tr>

          <tr v-if="median">
            <td>Median</td>
            <td></td>
            <td></td>
            <td>{{ formatPercentage(median.chairUtilizationRate) }}</td>
            <td></td>
          </tr>

          <tr v-if="averageTop10Percent">
            <td>Average top 10 percent</td>
            <td></td>
            <td></td>
            <td>{{ formatPercentage(averageTop10Percent.chairUtilizationRate) }}</td>
            <td></td>
          </tr>
        </template>

        <tr v-if="clinicMetricsByName">
          <td>Whole clinic</td>
          <td>{{ formatMinutes(clinicMetricsByName.productiveMinutes) }}</td>
          <td>{{ formatMinutes(clinicMetricsByName.chairUtilizationMinutes) }}</td>
          <td>{{ formatPercentage(clinicMetricsByName.chairUtilizationRate) }}</td>
          <td>{{ formatMoney(clinicMetricsByName.untappedPotential) }}</td>
        </tr>

        <NoRecordsFound
          v-else
          colspan="5"
        />
      </tbody>
    </ReportTable>

    <MetricDefinitionList
      v-if="!loading"
      :metrics="tooltips"
    />
  </ReportWrapper>
</template>

<script>
import DataService from '../../../../_services/DataService';
import MetricDefinitionList from '../../../components/Reporting/MetricDefinitionList';
import NoRecordsFound from '../../../components/Reporting/NoRecordsFound';
import ReportTable from '../../../components/Reporting/ReportTable.vue';
import ReportWrapper from '../../../components/Reporting/ReportWrapper';
import TableHeaderWithTooltip from '../../../components/Reporting/TableHeaderWithTooltip';
import Utils from '../../../utils';

export default {
  name: 'DDSChairUtilization',
  components: {
    MetricDefinitionList,
    NoRecordsFound,
    ReportTable,
    ReportWrapper,
    TableHeaderWithTooltip,
  },
  inject: ['shouldShowHiddenProviders'],
  props: ['tabLv1Index', 'tabLv2Index'],
  data() {
    return {
      dataPerProviderAll: [],
      userRole: '',
      userRecord: null,
      averageTop10Percent: null,
      median: null,
      loading: false,
      tooltips: {
        'Productive hours': 'Time scheduled for patient treatment (can generate production)',
        'Chair utilization hours': 'Time when the chairs are used',
        'Chair utilization %': 'Chair utilization hours / productive hours',
        'Untapped potential $': 'Potential $ dollar amount if the chairs are utilized in the free time (business hours - productive hours)',
      },
    };
  },
  computed: {
    isAdmin() {
      return this.userRole === 'clientAdmin';
    },
    userHasRecordInReport() {
      return !!this.userRecord;
    },
    showHiddenProviders() {
      return this.shouldShowHiddenProviders();
    },
    dataPerProvider() {
      return this.showHiddenProviders
        ? this.dataPerProviderAll
        : this.dataPerProviderAll.filter(this.providerIsVisible);
    },
  },
  methods: {
    async loadReport(dateRange) {
      const { start, end } = dateRange;
      this.loading = true;

      const result = await DataService.getRDHChairUtilizationReport({ start, end });

      if (result.status === 200) {
        const dataPerProvider = result.data.dataPerProvider ?? [];
        this.dataPerProviderAll = dataPerProvider;

        Utils.prependCurrentUserRecordAndMedian(dataPerProvider, result.data.median);

        this.clinicMetricsByName = result.data.clinicMetricsByName;
        this.userRecord = result.data.userRecord;
        this.averageTop10Percent = result.data.averageTop10Percent;
        this.median = result.data.median;
      } else {
        this.dataPerProviderAll = [];
      }

      this.loading = false;
    },
    formatMoney(number) {
      return Utils.formatCurrency(number, false, false);
    },
    formatMinutes(minutes) {
      return Utils.formatMinutes(minutes);
    },
    formatPercentage(number) {
      return Utils.formatPercentage(number ?? 0);
    },
    providerIsVisible(provider) {
      return provider.isActive && !provider.isHidden && !provider.isHiddenReport;
    },
  },
  mounted() {
    this.userRole = DataService.getRole();
  },
};
</script>
